import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createSubCategory,
  deleteSubCategory,
  updateSubCategory,
} from "../../State/SubCategory/Action";
import { getAllCategory } from "../../State/Category/Action";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Tooltip,
  IconButton
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { Add, Close, Delete, Edit, FileDownload, PictureAsPdf, Print } from "@mui/icons-material";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";

const AddSubCategory = () => {
  const dispatch = useDispatch();

  const { adminCategories } = useSelector((store) => store);

  const [subCategoryData, setSubCategoryData] = useState({
    categoryName: "",
  });
  const [selectedCategoryId, setSelectedCategoryId] = useState(0);

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subCategoryToDelete, setSubCategoryToDelete] = useState(null);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    dispatch(getAllCategory());
  }, [dispatch]);

  const categoryChanged = (e) => {
    setSelectedCategoryId(e.target.value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSubCategoryData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!subCategoryData.categoryName.trim()) {
      toast.error("SubCategory Name is required!!");
      return;
    }

    if (
      subCategoryData.categoryName.length < 3 ||
      subCategoryData.categoryName.length > 50
    ) {
      toast.error("SubCategory name must be between 3 and 50 characters.");
      return;
    }

    if (selectedCategoryId === 0) {
      toast.error("Please select a category.");
      return;
    }

    if (editingId) {
      dispatch(updateSubCategory(editingId, subCategoryData));
    } else {
      dispatch(createSubCategory(subCategoryData, selectedCategoryId));
    }

    handleCancle();
  };

  const handleCategoryUpdate = (subCategory, category) => {
    setSubCategoryData({ categoryName: subCategory.name });
    setSelectedCategoryId(category.id);
    setEditingId(subCategory.id);

    setOpenFormModal(true);
  };

  const handleOpenDeleteDialog = (subCategoryId) => {
    setSubCategoryToDelete(subCategoryId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteSubCategory(subCategoryToDelete));
    setDeleteDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSubCategoryToDelete(null);
  };

  const handleCancle = () => {
    setSubCategoryData({ categoryName: "" });
    setSelectedCategoryId(0);
    setEditingId(null);
    setOpenFormModal(false);
  }

  // Generate rows for DataGrid
  const rows = [];
  adminCategories.categories?.response?.forEach((category) => {
    category.subCategories?.forEach((subCategory, index) => {
      rows.push({
        id: subCategory.id,
        categoryName: category.name,
        subCategoryName: subCategory.name,
        rawSubCategory: subCategory,
        rawCategory: category,
      });
    });
  });

  const writeAction = PermissionService.has({ category: "Admin", page: "SubCategories", type: "WRITE" });

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "categoryName", headerName: "Category Name", flex: 1 },
    { field: "subCategoryName", headerName: "SubCategory Name", flex: 1 },
    ...(writeAction ? [
      {
        field: "actions",
        headerName: "Action",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box display="flex" gap={0} sx={{ p: 0, m: 0, minHeight: 0 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleCategoryUpdate(params.row.rawSubCategory, params.row.rawCategory)}
              >
                <Edit fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleOpenDeleteDialog(params.row.id)}
              >
                <Delete fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
    ] : []),
  ];

  const filteredRows = rows.filter(row => {
    const query = searchText.toLowerCase();
    return (
      row.categoryName?.toLowerCase().includes(query.toLowerCase()) ||
      row.subCategoryName?.toLowerCase().includes(query.toLowerCase())
    );
  });

    const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => [index + 1, row.categoryName, row.subCategoryName].join(',')).join('\n');
    const headers = ['S.No.', 'Category Name', 'Sub_Category Name'].join(',');
    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'subCategories.csv';
    link.click();
  };
  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Sub_Category List", 14, 15);
    const tableColumn = ['S.No.', 'Category Name', 'Sub_Category Name'];
    const tableRows = exportRows.map((row, index) => [index + 1, row.categoryName, row.subCategoryName]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      styles: { fontSize: 8, cellPadding: 1, overflow: 'linebreak' },
    });
    doc.save('subCategories.pdf');
  };
  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const headers = ['S.No.', 'Category Name', 'Sub_Category Name'];
    const tableContent = exportRows.map((row, index) => [index + 1, row.categoryName, row.subCategoryName]);
    const html = `
           <html>
             <head>
               <title>Sub_Category List</title>
               <style>
                 table { border-collapse: collapse; width: 100%; }
                 th, td { border: 1px solid black; padding: 8px; text-align: left; }
                 th {background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact; }
                 tr:nth-child(even) { background-color: #f5f5f5; }
               </style>
             </head>
             <body>
               <h2>Sub_Category List</h2>
               <table>
                 <thead>
                   <tr>
                     ${headers.map(header => `<th>${header}</th>`).join('')}
                   </tr>
                 </thead>
                 <tbody>
                   ${tableContent.map(row => `
                     <tr>
                       ${row.map(cell => `<td>${cell}</td>`).join('')}
                     </tr>
                   `).join('')}
                 </tbody>
               </table>
             </body>
           </html>
         `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div>
      <Card className="p-3" sx={{ borderRadius: 2, boxShadow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <CardHeader
            title={<Typography variant="h6" className="text-gray-700">SUB_CATEGORIES</Typography>}
            sx={{ p: 0 }}
          />
          {writeAction && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                sx={{
                  background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                    boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                    transform: "scale(1.02)",
                  },
                  cursor: "pointer",
                  p: 1,
                  borderRadius: '50%',
                }}
                onClick={() => {
                  setOpenFormModal(true);
                  setEditingId(null);
                }}
              >
                <Add />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#449AEF", fontSize: "1.2rem", letterSpacing: 0.2 }}
              >
                ADD SUB_CATEGORY
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleExportCSV}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Print
            </Button>
          </Box>
          <Box>
            <TextField
              label="Search Sub Categories"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              sx={{ width: { xs: '100%', sm: '300px' } }}
            />
          </Box>
        </Box>
        <Box sx={{ height: "100%", width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50, 75, 100]}
            getRowId={(row) => row.id}
            checkboxSelection
            onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
            rowSelectionModel={selectedRowIds}
            disableSelectionOnClick
            density="compact"
            experimentalFeatures={{ virtualization: true }}
            loading={adminCategories.loading}
            autoHeight={true}
            sx={{
              borderRadius: 2,
              fontSize: "0.8rem",
              "& .MuiDataGrid-cell": {
                fontSize: "0.8rem",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
                paddingTop: "4px",
                paddingBottom: "4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#449AEF",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.8rem",
                padding: "8px",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "0.8rem",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row": {
                maxHeight: "36px !important",
                minHeight: "36px !important",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#ffffff",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this SubCategory?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replace the form rendering with a Dialog: */}
      <Dialog
        open={openFormModal}
        onClose={handleCancle}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '1.25rem',
            fontWeight: 'bold',
            mt: 1,
            mb: 0.5,
            pb: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          {editingId ? "UPDATE" : "ADD"} SUBCATEGORY
          <IconButton onClick={handleCancle} size="small" sx={{ ml: 2 }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1 }}>
          <Card className="mb-4 mt-4" sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={selectedCategoryId}
                        onChange={categoryChanged}
                        label="Category"
                        disabled={editingId}
                      >
                        <MenuItem value={0} disabled>
                          Select Category...
                        </MenuItem>
                        {adminCategories?.categories?.response?.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="SubCategory Name"
                      name="categoryName"
                      value={subCategoryData.categoryName}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2, }}>
                    <Button
                      variant="contained"
                      type="submit"
                      sx={{
                        background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                          boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                          transform: "scale(1.02)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      {editingId ? "Update " : "Add "} SubCategory
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancle}
                      sx={{
                        color: "#449AEF",
                        borderColor: "#449AEF",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "#449AEF",
                          color: "#fff",
                          borderColor: "#449AEF",
                          boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>

                </Grid>
              </form>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AddSubCategory;
